import React, { useState, useEffect, useCallback, useRef } from 'react';
import './AdminDocumentLibrary.css';
import api from '../services/api';
import Sidebar from './Sidebar';
import { useMobile } from '../hooks/useMobile';

// Document-type categories offered in the dropdown.
const CATEGORIES = [
  { value: 'identity', label: 'Identity' },
  { value: 'income', label: 'Income' },
  { value: 'bank', label: 'Bank / Statements' },
  { value: 'property', label: 'Property' },
  { value: 'other', label: 'Other' },
];

const BLANK_FORM = {
  name: '',
  category: 'identity',
  description: '',
  is_required: true,
  accepts_multiple: false,
  allowed_extensions: 'pdf,jpg,jpeg,png',
  max_file_size_mb: 10,
  ocr_enabled: false,
  expected_keywords: '',
  sort_order: 0,
  is_active: true,
};

export default function AdminDocumentLibrary() {
  const mobile = useMobile();
  const [docTypes, setDocTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(BLANK_FORM);
  const [saving, setSaving] = useState(false);

  // ── drag-and-drop reorder ──
  const dragId = useRef(null);

  const handleDragStart = (id) => { dragId.current = id; };

  const handleDrop = async (targetId) => {
    if (!dragId.current || dragId.current === targetId) return;
    const from = docTypes.findIndex(d => d.id === dragId.current);
    const to   = docTypes.findIndex(d => d.id === targetId);
    if (from < 0 || to < 0) return;

    // Optimistic update
    const reordered = [...docTypes];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);
    setDocTypes(reordered);

    dragId.current = null;

    try {
      await api.patch('/admin/document-types/reorder', { ids: reordered.map(d => d.id) });
    } catch (err) {
      flash(setError, 'Reorder failed — refreshing');
      load();
    }
  };

  const flash = (setter, msg) => {
    setter(msg);
    setTimeout(() => setter(''), 4000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/document-types');
      setDocTypes(data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load document types');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setForm(BLANK_FORM);
    setShowForm(true);
  };

  const openEdit = (dt) => {
    setEditingId(dt.id);
    setForm({
      name: dt.name || '',
      category: dt.category || 'other',
      description: dt.description || '',
      is_required: !!dt.is_required,
      accepts_multiple: !!dt.accepts_multiple,
      allowed_extensions: dt.allowed_extensions || 'pdf,jpg,jpeg,png',
      max_file_size_mb: dt.max_file_size_mb ?? 10,
      ocr_enabled: !!dt.ocr_enabled,
      expected_keywords: dt.expected_keywords || '',
      sort_order: dt.sort_order ?? 0,
      is_active: dt.is_active !== false,
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(BLANK_FORM);
  };

  const handleChange = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      flash(setError, 'Name is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        max_file_size_mb: Number(form.max_file_size_mb) || 10,
        sort_order: Number(form.sort_order) || 0,
        description: form.description.trim() || null,
        expected_keywords: form.expected_keywords.trim() || null,
      };
      if (editingId) {
        await api.put(`/admin/document-types/${editingId}`, payload);
        flash(setSuccess, 'Document type updated');
      } else {
        await api.post('/admin/document-types', payload);
        flash(setSuccess, 'Document type created');
      }
      closeForm();
      load();
    } catch (err) {
      flash(setError, err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (dt) => {
    if (!window.confirm(`Delete "${dt.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/document-types/${dt.id}`);
      flash(setSuccess, 'Document type deleted');
      load();
    } catch (err) {
      flash(setError, err.response?.data?.error || 'Delete failed');
    }
  };

  const categoryLabel = (val) =>
    CATEGORIES.find((c) => c.value === val)?.label || val || '—';

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#F7FAFC' }}>
      <Sidebar />
      <main style={{
        flex: 1,
        marginLeft: mobile ? 0 : 70,
        padding: mobile ? '70px 16px 32px' : '32px 40px',
        width: mobile ? '100%' : 'calc(100% - 70px)',
        boxSizing: 'border-box',
      }}>
      <div className="bc-doclib">
      <div className="bc-doclib__header">
        <div>
          <h2 className="bc-doclib__title">Document Library</h2>
          <p className="bc-doclib__subtitle">
            Define the documents your team collects against each deal.
          </p>
        </div>
        <button className="bc-btn bc-btn--primary" onClick={openCreate}>
          + New document type
        </button>
      </div>

      {error && <div className="bc-alert bc-alert--error">{error}</div>}
      {success && <div className="bc-alert bc-alert--success">{success}</div>}

      {loading ? (
        <div className="bc-doclib__empty">Loading…</div>
      ) : docTypes.length === 0 ? (
        <div className="bc-doclib__empty">
          No document types yet. Create your first one to get started.
        </div>
      ) : (
        <div className="bc-doclib__table-wrap">
          <table className="bc-doclib__table">
            <thead>
              <tr>
                <th aria-label="Drag" style={{ width: 32 }} />
                <th>Name</th>
                <th>Category</th>
                <th>Required</th>
                <th>Multiple</th>
                <th>Formats</th>
                <th>Max MB</th>
                <th>OCR</th>
                <th>Status</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {docTypes.map((dt) => (
                <tr
                  key={dt.id}
                  className={dt.is_active ? 'bc-doclib__row--draggable' : 'bc-doclib__row--inactive bc-doclib__row--draggable'}
                  draggable
                  onDragStart={() => handleDragStart(dt.id)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => handleDrop(dt.id)}
                >
                  <td className="bc-doclib__drag-handle" title="Drag to reorder">⠿</td>
                  <td>
                    <span className="bc-doclib__name">{dt.name}</span>
                    {dt.description && (
                      <span className="bc-doclib__desc">{dt.description}</span>
                    )}
                  </td>
                  <td>{categoryLabel(dt.category)}</td>
                  <td>{dt.is_required ? 'Yes' : 'No'}</td>
                  <td>{dt.accepts_multiple ? 'Yes' : 'No'}</td>
                  <td className="bc-doclib__mono">{dt.allowed_extensions}</td>
                  <td>{dt.max_file_size_mb}</td>
                  <td>{dt.ocr_enabled ? 'On' : 'Off'}</td>
                  <td>
                    <span className={`bc-badge ${dt.is_active ? 'bc-badge--active' : 'bc-badge--neutral'}`}>
                      {dt.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="bc-doclib__actions">
                    <button className="bc-btn bc-btn--ghost" onClick={() => openEdit(dt)}>Edit</button>
                    <button className="bc-btn bc-btn--danger-ghost" onClick={() => handleDelete(dt)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div className="bc-modal__overlay" onClick={closeForm}>
          <div className="bc-modal" onClick={(e) => e.stopPropagation()}>
            <div className="bc-modal__head">
              <h3>{editingId ? 'Edit document type' : 'New document type'}</h3>
              <button className="bc-modal__close" onClick={closeForm} aria-label="Close">×</button>
            </div>

            <div className="bc-modal__body">
              <label className="bc-field">
                <span className="bc-field__label">Name *</span>
                <input
                  className="bc-input"
                  value={form.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="e.g. Photo ID, Latest payslip"
                  autoFocus
                />
              </label>

              <label className="bc-field">
                <span className="bc-field__label">Category</span>
                <select
                  className="bc-input"
                  value={form.category}
                  onChange={(e) => handleChange('category', e.target.value)}
                >
                  {CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </label>

              <label className="bc-field">
                <span className="bc-field__label">Description</span>
                <textarea
                  className="bc-input"
                  rows={2}
                  value={form.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder="Optional guidance shown to the team"
                />
              </label>

              <div className="bc-field-row">
                <label className="bc-field">
                  <span className="bc-field__label">Allowed formats</span>
                  <input
                    className="bc-input"
                    value={form.allowed_extensions}
                    onChange={(e) => handleChange('allowed_extensions', e.target.value)}
                    placeholder="pdf,jpg,png"
                  />
                </label>
                <label className="bc-field bc-field--narrow">
                  <span className="bc-field__label">Max size (MB)</span>
                  <input
                    className="bc-input"
                    type="number"
                    min="1"
                    value={form.max_file_size_mb}
                    onChange={(e) => handleChange('max_file_size_mb', e.target.value)}
                  />
                </label>
                <label className="bc-field bc-field--narrow">
                  <span className="bc-field__label">Sort order</span>
                  <input
                    className="bc-input"
                    type="number"
                    value={form.sort_order}
                    onChange={(e) => handleChange('sort_order', e.target.value)}
                  />
                </label>
              </div>

              <label className="bc-field">
                <span className="bc-field__label">
                  Expected keywords <span className="bc-field__hint">(for future OCR validation)</span>
                </span>
                <input
                  className="bc-input"
                  value={form.expected_keywords}
                  onChange={(e) => handleChange('expected_keywords', e.target.value)}
                  placeholder="comma,separated,hints"
                />
              </label>

              <div className="bc-toggles">
                <label className="bc-toggle">
                  <input type="checkbox" checked={form.is_required}
                    onChange={(e) => handleChange('is_required', e.target.checked)} />
                  <span>Required</span>
                </label>
                <label className="bc-toggle">
                  <input type="checkbox" checked={form.accepts_multiple}
                    onChange={(e) => handleChange('accepts_multiple', e.target.checked)} />
                  <span>Accepts multiple files</span>
                </label>
                <label className="bc-toggle">
                  <input type="checkbox" checked={form.ocr_enabled}
                    onChange={(e) => handleChange('ocr_enabled', e.target.checked)} />
                  <span>OCR enabled</span>
                </label>
                <label className="bc-toggle">
                  <input type="checkbox" checked={form.is_active}
                    onChange={(e) => handleChange('is_active', e.target.checked)} />
                  <span>Active</span>
                </label>
              </div>
            </div>

            <div className="bc-modal__foot">
              <button className="bc-btn bc-btn--ghost" onClick={closeForm} disabled={saving}>Cancel</button>
              <button className="bc-btn bc-btn--primary" onClick={handleSubmit} disabled={saving}>
                {saving ? 'Saving…' : editingId ? 'Save changes' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
      </main>
    </div>
  );
}
